import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { solicitudesAPI } from '../../lib/api';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { estadoColor, estadoLabel } from './SolicitudesDashboard';
import { Search, Eye, Clock } from 'lucide-react';

export default function SolicitudesListPage() {
    const [solicitudes, setSolicitudes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filtroEstado, setFiltroEstado] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const params = {};
        if (filtroEstado) params.estado = filtroEstado;
        solicitudesAPI.listar(params)
            .then(r => setSolicitudes(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [filtroEstado]);

    const filtered = solicitudes.filter(s => {
        const q = search.toLowerCase();
        return !q ||
            s.solicitante?.nombre?.toLowerCase().includes(q) ||
            s.solicitante?.email?.toLowerCase().includes(q) ||
            s.solicitante?.empresa?.toLowerCase().includes(q) ||
            s.tipo_nombre?.toLowerCase().includes(q) ||
            s.id?.toLowerCase().includes(q);
    });

    if (loading) return <Layout><div className="flex items-center justify-center h-64"><Clock className="h-6 w-6 animate-spin text-primary" /></div></Layout>;

    return (
        <Layout>
        <div className="space-y-4" data-testid="solicitudes-list">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold">Todas las Solicitudes</h1>
                <Badge variant="outline">{filtered.length} solicitudes</Badge>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Buscar por solicitante, email, empresa..."
                        value={search} onChange={e => setSearch(e.target.value)}
                        className="pl-10"
                        data-testid="search-solicitudes"
                    />
                </div>
                <div className="flex gap-2 flex-wrap">
                    {['', 'creada', 'en_verificacion', 'aprobada', 'rechazada'].map(e => (
                        <Button key={e} size="sm"
                            variant={filtroEstado === e ? 'default' : 'outline'}
                            onClick={() => setFiltroEstado(e)}
                        >
                            {e ? estadoLabel[e] : 'Todos'}
                        </Button>
                    ))}
                </div>
            </div>

            <div className="space-y-2">
                {filtered.length === 0 ? (
                    <Card><CardContent className="py-8 text-center text-muted-foreground">No hay solicitudes</CardContent></Card>
                ) : filtered.map(s => (
                    <Card key={s.id} className="hover:border-primary/40 transition-colors cursor-pointer"
                        onClick={() => navigate(`/solicitudes/detalle/${s.id}`)}>
                        <CardContent className="py-3 flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="font-medium truncate">{s.solicitante?.nombre || 'Sin nombre'}</span>
                                    <Badge className={`text-xs ${estadoColor[s.estado]}`}>{estadoLabel[s.estado]}</Badge>
                                </div>
                                <div className="text-sm text-muted-foreground flex gap-3 flex-wrap">
                                    <span>{s.tipo_nombre}</span>
                                    <span>{s.solicitante?.empresa}</span>
                                    <span>{new Date(s.created_at).toLocaleDateString('es-CL')}</span>
                                </div>
                            </div>
                            <Button size="sm" variant="ghost"><Eye className="h-4 w-4" /></Button>
                        </CardContent>
                    </Card>
                ))}
            </div>
        </div>
        </Layout>
    );
}
